"use client";

import cn from "classnames";

import { Link } from "@/components/link/link.component";

import { appendQueryString } from "@/utils/url.util";

import type { HeroSection as HeroSectionType } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

import { RichText } from "../richText/richText.component";
import { SplitHeroSection } from "./splitHeroSection.component";

export const HeroSection = ({ block, pageName }: HeroSectionProps) => {
  if (block?.layout === "split") {
    return <SplitHeroSection block={block} pageName={pageName} />;
  }

  const backgroundImageUrl = block?.backgroundImage
    ? urlFor(block.backgroundImage).width(1920).height(800).url()
    : null;

  const mobileBackgroundImageUrl = block?.mobileBackgroundImage
    ? urlFor(block.mobileBackgroundImage)
        .width(800)
        .height(1000)
        .fit("max")
        .url()
    : null;

  const ctaHref =
    block?.ctaLink?.url != null
      ? appendQueryString(block.ctaLink.url, block.ctaLink.queryString)
      : null;

  return (
    <section
      data-testid="hero-section"
      className="bg-secondary relative flex min-h-[max(21rem,48svh)] w-full flex-col overflow-hidden sm:min-h-[max(22rem,52svh)] md:min-h-[max(24rem,50svh)] lg:min-h-[max(36rem,50vh)]">
      {backgroundImageUrl ? (
        <>
          {mobileBackgroundImageUrl ? (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 z-0 bg-cover bg-center bg-no-repeat lg:hidden"
              style={{
                backgroundImage: `url(${mobileBackgroundImageUrl})`,
              }}
            />
          ) : null}
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-0 z-0 bg-cover bg-center bg-no-repeat lg:bg-right",
              mobileBackgroundImageUrl && "hidden lg:block",
            )}
            style={{
              backgroundImage: `url(${backgroundImageUrl})`,
            }}
          />
        </>
      ) : null}

      {!mobileBackgroundImageUrl ? (
        <div
          className="pointer-events-none absolute inset-0 lg:hidden"
          style={{
            backgroundImage:
              "linear-gradient(to top, rgba(0, 34, 106, 0.95) 0%, rgba(0, 34, 106, 0.3) 60%, rgba(0, 34, 106, 0) 100%)",
          }}
        />
      ) : (
        <div
          className="pointer-events-none absolute inset-0 lg:hidden"
          style={{
            backgroundImage:
              "linear-gradient(to top, rgba(0, 34, 106, 0.95) 0%,  rgba(0, 34, 106, .3) 20%)",
          }}
        />
      )}
      <div
        className="pointer-events-none absolute inset-0 hidden lg:block"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(0, 34, 106, 0.9) 0%, rgba(0, 34, 106, 0) 40%)",
        }}
      />

      <div className="relative container mx-auto flex min-h-0 w-full flex-1 flex-col justify-end px-6 pt-44 pb-10 lg:justify-center lg:pt-16">
        <div className="flex max-w-2xl flex-col lg:mt-10">
          <div
            className="mb-4 sm:leading-[1.15]! lg:text-[64px]!"
            data-testid="hero-section-title">
            <RichText>{block?.title}</RichText>
          </div>

          {block?.subtitle && (
            <div
              className="font-brown-std mb-8 text-[14px] font-bold lg:mb-6 lg:w-xl lg:pr-14 lg:text-[16px]"
              data-testid="hero-section-subtitle">
              <RichText>{block.subtitle}</RichText>
            </div>
          )}

          {block?.ctaLabel && ctaHref ? (
            <Link
              data-testid="hero-section-cta"
              primary
              href={ctaHref}
              className="w-fit min-w-40 px-10 py-3 uppercase"
              target={block?.ctaLink?.openInNewTab ? "_blank" : undefined}
              rel={
                block?.ctaLink?.openInNewTab ? "noopener noreferrer" : undefined
              }>
              {block.ctaLabel}
            </Link>
          ) : null}

          {block?.legalCallout && (
            <div data-testid="hero-section-legal-callout" className="mt-2">
              <RichText>{block.legalCallout}</RichText>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export type HeroSectionProps = {
  block: HeroSectionType;
  pageName?: string | null;
};
